import { useQuery } from "@tanstack/react-query";
import { getDiffDay } from "../../../helper/dateTimeHelper";
import { useSearchParams } from "../../../hook/use-search-params";
import { DocumentService } from "../../../services/document/document.service";
import { useMemo } from "react";
import { DateHelper } from "../../../helper";
import moment from "moment";

const isDocumentExpired = (effectiveTo: string) => {
  const today = new Date();
  return getDiffDay(effectiveTo, today.toDateString()) >= 0;
};

export const usePublishDocumentsInfo = () => {
  const { searchParams } = useSearchParams();
  const start = searchParams.get("start") ?? "";
  const end = searchParams.get("end") ?? "";

  const { data, isLoading } = useQuery({
    queryKey: ["publish-documents-info"],
    queryFn: () => DocumentService.getListPublicDocumentInfo(),
    select: (data) => data.data,
  });

  const filteredData = useMemo(
    () => 
      end && start
        ? data?.filter((e: any) => DateHelper.isDateInRange(moment(e.effectiveFrom), moment(start), moment(end)))
        : data,
    [data, end, start],
  );

  const internalDocument = useMemo(() => filteredData?.filter((e: any) => e.docTypeId === "1"), [filteredData]);

  const countInternalDocument = useMemo(() => internalDocument?.length, [internalDocument?.length]);

  const externalDocument = useMemo(() => filteredData?.filter((e: any) => e.docTypeId === "2"), [filteredData]);

  const sqmsDocument = useMemo(() => filteredData?.filter((e: any) => e.docTypeId === "5" || e.docTypeId === "58"), [filteredData]);
  const countSQMSDocument = useMemo(() => sqmsDocument?.length, [sqmsDocument?.length]);
  const validSQMSDocuments = useMemo(() => sqmsDocument?.filter((e: any) => !isDocumentExpired(e.effectiveTo)), [sqmsDocument]);
  const countValidSQMSDocument = useMemo(() => sqmsDocument?.length, [sqmsDocument?.length]);
  const expiredSQMSDocuments = useMemo(
    () =>
      sqmsDocument?.filter(
        (e: any) =>
          isDocumentExpired(e.effectiveTo) && (e.docStatusValue === "publish" || e.docStatusValue === "interimpublish"),
      ),
    [sqmsDocument],
  );

  const countExpiredSQMSDocument = useMemo(
    () => expiredSQMSDocuments?.length,
    [expiredSQMSDocuments?.length],
  );

  const countExternalDocument = useMemo(() => externalDocument?.length, [externalDocument?.length]);

  const validInternalDocuments = useMemo(
    () => internalDocument?.filter((e: any) => !isDocumentExpired(e.effectiveTo)),
    [internalDocument],
  );

  const countValidInternalDocument = useMemo(() => validInternalDocuments?.length, [validInternalDocuments?.length]);

  const expiredInternalDocuments = useMemo(
    () =>
      internalDocument?.filter(
        (e: any) =>
          isDocumentExpired(e.effectiveTo) && (e.docStatusValue === "publish" || e.docStatusValue === "interimpublish"),
      ),
    [internalDocument],
  );

  const countExpiredInternalDocument = useMemo(
    () => expiredInternalDocuments?.length,
    [expiredInternalDocuments?.length],
  );

  const validExternalDocuments = useMemo(
    () => externalDocument?.filter((e: any) => !isDocumentExpired(e.effectiveTo)),
    [externalDocument],
  );

  const countValidExternalDocument = useMemo(() => validExternalDocuments?.length, [validExternalDocuments?.length]);

  const expiredExternalDocuments = useMemo(
    () =>
      externalDocument?.filter(
        (e: any) =>
          isDocumentExpired(e.effectiveTo) && (e.docStatusValue === "publish" || e.docStatusValue === "interimpublish"),
      ),
    [externalDocument],
  );

  const countExpiredExternalDocument = useMemo(
    () => expiredExternalDocuments?.length,
    [expiredExternalDocuments?.length],
  );

  return {
    filteredData,
    isLoading,
    internalDocument,
    countInternalDocument,
    externalDocument,
    countExternalDocument,
    validInternalDocuments,
    countValidInternalDocument,
    expiredInternalDocuments,
    countExpiredInternalDocument,
    validExternalDocuments,
    countValidExternalDocument,
    expiredExternalDocuments,
    countExpiredExternalDocument,
    countSQMSDocument,
    validSQMSDocuments,
    countValidSQMSDocument,
    sqmsDocument,
    expiredSQMSDocuments,
    countExpiredSQMSDocument,
  };
};
